import axiosInstance from '../utils/AxiosConfig';

const unwrap = (response) => response.data?.data ?? response.data;

/**
 * Whether the terminal sells with an integrated POS and which one: `{ enabled, provider }`, where `provider` describes it
 * (code, displayName, currencies, supportsBatchClose...). The billing flow adapts to that instead of knowing providers.
 */
export const getPosAvailability = async (locationId, terminalCode) => {
  if (!locationId || !terminalCode) return { enabled: false, provider: null };
  const response = await axiosInstance.get('/payments/pos/availability', {
    params: { locationId, terminalCode },
    skipGlobalErrorRedirect: true,
  });
  const data = unwrap(response);
  const enabled = data?.enabled === true;
  return { enabled, provider: enabled ? data?.provider ?? null : null };
};

export const startPosPayment = async (payload) => {
  // Failures are shown inside the billing modal, never as the global 500 page.
  const response = await axiosInstance.post('/payments/pos/authorize', payload, { skipGlobalErrorRedirect: true });
  return unwrap(response);
};

export const pollPosPayment = async (payload) => {
  const response = await axiosInstance.post('/payments/pos/poll', payload, {
    skipGlobalErrorRedirect: true,
  });
  return unwrap(response);
};

export const cancelPosPayment = async (payload) => {
  const response = await axiosInstance.post('/payments/pos/cancel', payload, {
    skipGlobalErrorRedirect: true,
  });
  return unwrap(response);
};

export const getUnbilledPosPayments = async (locationId, terminalCode) => {
  if (!locationId || !terminalCode) return [];
  const response = await axiosInstance.get('/payments/pos/unbilled', {
    params: { locationId, terminalCode },
    skipGlobalErrorRedirect: true,
  });
  const payments = unwrap(response);
  return Array.isArray(payments) ? payments : [];
};

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class PosPaymentTimeoutError extends Error {
  constructor() {
    super('No se pudo confirmar el resultado del cobro con el POS.');
    this.name = 'PosPaymentTimeoutError';
  }
}

/**
 * Asks the backend for the state of a sale until it is finished. Each call is a short, idempotent
 * consult, so a dropped request (network blip, restart) is simply asked again: the server keeps the
 * sale, and anything we stop waiting for is resolved by its own reconciliation. Business errors
 * (4xx) are not retried.
 */
export const waitForPosPayment = async ({
  locationId,
  terminalCode,
  transactionId,
  deadlineMs = 150_000,
  onProgress,
  sleep = defaultSleep,
  now = Date.now,
}) => {
  const startedAt = now();
  let failures = 0;
  let waitMs = 2000;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const status = await pollPosPayment({
        location_id: parseInt(locationId),
        terminal_code: terminalCode,
        transaction_id: transactionId,
      });
      failures = 0;
      onProgress?.(status);
      if (status?.finished) return status;
      waitMs = Math.min(5000, Math.max(1000, Number(status?.secondsToRepoll || 2) * 1000));
    } catch (error) {
      const httpStatus = error?.response?.status;
      if (httpStatus && httpStatus >= 400 && httpStatus < 500) throw error;
      failures += 1;
      waitMs = Math.min(5000, 1000 * failures);
    }
    if (now() - startedAt >= deadlineMs) throw new PosPaymentTimeoutError();
    await sleep(waitMs);
  }
};
